function camelCase(input = '') {
  return input
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index === 0) {
        return lower;
      }
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

/**
 * Build a safe field name from user-provided label.
 * Falls back to camelCase plus random suffix when needed.
 */
export function generateAttributeFieldName(label = '', existingNames = []) {
  const base = camelCase(label) || 'customField';
  let candidate = `attr_${base}`;
  let counter = 1;

  const exists = (name) => existingNames.includes(name);
  while (exists(candidate)) {
    counter += 1;
    candidate = `attr_${base}_${counter}`;
  }
  return candidate;
}

/**
 * Determine whether a field definition represents an attribute stored
 * in the shared Attributes JSON column.
 */
export function isCustomAttributeField(field = {}) {
  if (!field) return false;
  const metadata = field.metadata || field.meta || {};
  const storage = field.storage || metadata.storage || {};

  return (
    metadata.isCustomAttribute === true ||
    storage.type === 'attributes' ||
    storage.source === 'attributes' ||
    metadata.attributeSource === 'attributes'
  );
}

/**
 * Extract custom attribute fields from a merged entity configuration.
 * Supports tenant-aware filtering so that tenant-specific fields don't leak across tenants.
 *
 * @param {object} config - Entity configuration or wrapped response ({ config })
 * @param {object} options
 * @param {string|null} options.tenantCode - Current tenant code
 * @param {boolean} [options.includeGlobal=true] - Whether to include global/shared custom fields
 * @returns {Array<object>} Normalised list of custom attribute fields
 */
export function extractCustomAttributeFields(config, options = {}) {
  const { tenantCode = null, includeGlobal = true } = options;
  const resolvedConfig = config?.config || config;
  const configFields = resolvedConfig?.fields;

  if (!configFields) return [];

  const normalisedTenant =
    tenantCode != null
      ? String(tenantCode).trim().toLowerCase()
      : null;

  const shouldIncludeField = (field = {}) => {
    // If no tenant filtering requested, honour includeGlobal flag only
    if (!normalisedTenant) {
      if (includeGlobal) return true;

      const isTenantSpecific =
        field._tenantSpecific === true ||
        field._source === 'tenant_custom' ||
        field._tenantCode != null ||
        field._source_tenant != null ||
        field?.metadata?.tenantCode != null;

      return isTenantSpecific;
    }

    const sourceTenantRaw =
      field._tenantCode ??
      field._source_tenant ??
      field?.metadata?.tenantCode ??
      field?.metadata?.tenant ??
      null;

    const sourceTenant =
      sourceTenantRaw != null
        ? String(sourceTenantRaw).trim().toLowerCase()
        : null;

    const isTenantSpecific =
      field._tenantSpecific === true ||
      field._source === 'tenant_custom' ||
      sourceTenant !== null;

    if (!isTenantSpecific) {
      return includeGlobal;
    }

    if (!sourceTenant) {
      // Unknown tenant for tenant-specific field – assume it belongs to current tenant
      return true;
    }

    return sourceTenant === normalisedTenant;
  };

  return Object.entries(configFields)
    .filter(([, field]) => isCustomAttributeField(field))
    .filter(([, field]) => shouldIncludeField(field))
    .map(([name, field]) => {
      const attributeType =
        field?.metadata?.attributeType ||
        field?.ui?.inputType ||
        field?.dataType ||
        'text';

      const metadata = {
        ...(field.metadata || {}),
        isCustomAttribute: true,
        attributeType
      };

      const sourceTenant =
        field._tenantCode ??
        field._source_tenant ??
        metadata.tenantCode ??
        null;

      if (sourceTenant && !metadata.tenantCode) {
        metadata.tenantCode = sourceTenant;
      }

      if (
        (field._tenantSpecific === true || field._source === 'tenant_custom') &&
        metadata.tenantOnly === undefined
      ) {
        metadata.tenantOnly = true;
      }

      return {
        name,
        ...field,
        metadata,
        _source: field._source,
        _source_tenant: field._source_tenant,
        _tenantSpecific: field._tenantSpecific,
        _tenantCode: field._tenantCode
      };
    })
    .sort((a, b) => {
      const orderA = a.ui?.order ?? 999;
      const orderB = b.ui?.order ?? 999;
      if (orderA === orderB) {
        return a.displayName?.localeCompare(b.displayName || '') || 0;
      }
      return orderA - orderB;
    });
}

/**
 * Prepare attributes object for submission to API.
 */
export function sanitizeAttributePayload(attributes, fields = []) {
  if (!attributes || typeof attributes !== 'object') {
    return {};
  }

  const allowedNames = new Set(fields.map((field) => field.name));
  const payload = {};

  Object.entries(attributes).forEach(([key, rawValue]) => {
    if (!allowedNames.has(key)) return;

    const field = fields.find((f) => f.name === key);
    const attributeType =
      field?.metadata?.attributeType ||
      field?.ui?.inputType ||
      field?.dataType ||
      'text';

    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return;
    }

    if (attributeType === 'number') {
      const valueAsNumber = Number(rawValue);
      if (!Number.isNaN(valueAsNumber)) {
        payload[key] = valueAsNumber;
      }
      return;
    }

    payload[key] = rawValue;
  });

  return payload;
}

function getChoiceOptions(field = {}) {
  if (!field) return [];

  if (Array.isArray(field?.ui?.options)) {
    return field.ui.options.map((option) =>
      typeof option === 'string' ? { value: option, label: option } : option
    );
  }

  if (Array.isArray(field?.validation?.enum)) {
    return field.validation.enum.map((value) => ({
      value,
      label: value
    }));
  }

  return [];
}

/**
 * Predefined number format presets for quick selection
 */
export const NUMBER_FORMAT_PRESETS = {
  none: { 
    label: 'None (default)', 
    value: 'none',
    decimals: 0,
    useThousandsSeparator: false,
    prefix: '',
    suffix: ''
  },
  integer: { 
    label: 'Integer (no decimals)', 
    value: 'integer',
    decimals: 0,
    useThousandsSeparator: true,
    prefix: '',
    suffix: ''
  },
  decimal2: { 
    label: 'Decimal (2 places)', 
    value: 'decimal2',
    decimals: 2,
    useThousandsSeparator: true,
    prefix: '',
    suffix: ''
  },
  decimal4: { 
    label: 'Decimal (4 places)', 
    value: 'decimal4',
    decimals: 4,
    useThousandsSeparator: true,
    prefix: '',
    suffix: ''
  },
  currency: { 
    label: 'Currency (฿)', 
    value: 'currency',
    decimals: 2,
    useThousandsSeparator: true,
    prefix: '฿',
    suffix: ''
  },
  currencyUSD: { 
    label: 'Currency (USD)', 
    value: 'currencyUSD',
    decimals: 2,
    useThousandsSeparator: true,
    prefix: '$',
    suffix: ''
  },
  percentage: { 
    label: 'Percentage (%)', 
    value: 'percentage',
    decimals: 2,
    useThousandsSeparator: false,
    prefix: '',
    suffix: '%'
  },
  percentageDecimal: { 
    label: 'Percentage (decimal)', 
    value: 'percentageDecimal',
    decimals: 4,
    useThousandsSeparator: false,
    prefix: '',
    suffix: '%'
  },
  custom: { 
    label: 'Custom format', 
    value: 'custom'
  }
};

/**
 * Format a number according to format configuration
 * @param {number} value - The number to format
 * @param {object} formatConfig - Format configuration object
 * @returns {string} Formatted number string
 */
export function formatNumber(value, formatConfig = {}) {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  const numValue = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numValue)) {
    return String(value);
  }

  // If no format config, return as is
  if (!formatConfig || Object.keys(formatConfig).length === 0 || formatConfig.preset === 'none') {
    return String(numValue);
  }

  let decimals = formatConfig.decimals !== undefined ? formatConfig.decimals : 2;
  let useThousandsSeparator = formatConfig.useThousandsSeparator !== false;
  let prefix = formatConfig.prefix || '';
  let suffix = formatConfig.suffix || '';

  // Apply preset if specified
  if (formatConfig.preset && formatConfig.preset !== 'custom' && NUMBER_FORMAT_PRESETS[formatConfig.preset]) {
    const preset = NUMBER_FORMAT_PRESETS[formatConfig.preset];
    decimals = preset.decimals;
    useThousandsSeparator = preset.useThousandsSeparator;
    prefix = preset.prefix;
    suffix = preset.suffix;
  }

  // Format the number
  let formatted = numValue.toFixed(decimals);

  // Add thousands separator if needed
  if (useThousandsSeparator) {
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    formatted = parts.join('.');
  }

  // Add prefix and suffix
  return `${prefix}${formatted}${suffix}`;
}

/**
 * Format attribute value for display based on attribute type.
 */
export function formatAttributeValue(value, field = {}) {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  const attributeType =
    field?.metadata?.attributeType ||
    field?.ui?.inputType ||
    field?.dataType ||
    'text';

  switch (attributeType) {
    case 'number': {
      // Get number format config from field metadata
      const numberFormat = field?.metadata?.numberFormat || field?.ui?.numberFormat;
      if (numberFormat) {
        return formatNumber(value, numberFormat);
      }
      return value;
    }
    case 'date':
      try {
        return new Date(value).toLocaleDateString();
      } catch (err) {
        return value;
      }
    case 'choice':
    case 'select':
    case 'dropdown': {
      const options = getChoiceOptions(field);

      if (Array.isArray(value)) {
        const mapped = value
          .map((item) => {
            const option = options.find((opt) => opt.value === item);
            return option?.label ?? item;
          })
          .filter(Boolean);

        return mapped.length > 0 ? mapped.join(', ') : value.join(', ');
      }

      const option = options.find((opt) => opt.value === value);
      if (option) {
        return option.label ?? option.value ?? value;
      }

      return value;
    }
    case 'table': {
      // Table data is an array of objects
      if (!Array.isArray(value) || value.length === 0) {
        return '-';
      }
      // Return a summary like "3 rows"
      return `${value.length} row${value.length !== 1 ? 's' : ''}`;
    }
    default:
      // Handle objects that might be accidentally rendered
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
      }
      return value;
  }
}

export default {
  generateAttributeFieldName,
  isCustomAttributeField,
  extractCustomAttributeFields,
  sanitizeAttributePayload,
  formatAttributeValue,
  getChoiceOptions,
  formatNumber,
  NUMBER_FORMAT_PRESETS
};

export { getChoiceOptions };

